import React, { memo, useState, useRef, useMemo, useEffect } from 'react';

import CustomCheckbox from '@lcsc-ui/components/CustomCheckBox';
import useUserStore from '@lcsc-ui/store/user';
import { buriedPoint } from '@lcsc-ui/utils/buriedPoint';
import Utils from '@lcsc-ui/utils/Utils';
import { message, Popover } from 'antd';
import { cloneDeep } from 'lodash-es';

import { SortListEnums, StepEnums } from '../enums';
import { StyledSearchInput, StyledFilterCondition } from '../style';

import FilterConditionItem from './FilterConditionItem';
import GuideMask from './GuideMask';

import SvgIcon from '@/components/SvgIcon';

const getLetterList = (arr) => {
  const result = [];
  (arr || []).forEach((item) => {
    if (item.firstLetter && !result.includes(item.firstLetter)) {
      result.push(item.firstLetter);
    }
  });
  return result.sort();
};
const FilterCondition = (props) => {
  const { userInfo } = useUserStore();

  const { couponTypeList, sendSearchParams, changeSendSearchParams, couponCatalogList, couponBrandList } = props;
  const [inputValue, setInputValue] = useState('');
  const [showGuide, setShowGuide] = useState(false);
  const [stepNum, setStepNum] = useState(StepEnums.one);
  const [closeIconHoverStatus, setCloseIconHoverStatus] = useState(false);

  const catalogData = {
    name: '类目',
    value: 'value',
    label: 'label',
    list: couponCatalogList,
    letterList: getLetterList(couponCatalogList),
    showInputCondition: false,
    showCouponCheckbox: true,
  };
  const brandData = {
    name: '品牌',
    value: 'value',
    label: 'label',
    list: couponBrandList,
    letterList: getLetterList(couponBrandList),
    showInputCondition: false,
  };
  const catalogRef = useRef(null);
  const brandRef = useRef(null);
  const catalogLiRef = useRef(null);
  const brandLiRef = useRef(null);
  const searchInputRef = useRef(null);
  const changeCouponType = (item) => {
    const couponTypeState = sendSearchParams.couponType ? cloneDeep(sendSearchParams.couponType.split(',')) : [];
    if (couponTypeState.includes(item.couponType)) {
      const index = couponTypeState.findIndex((v) => v === item.couponType);
      couponTypeState.splice(index, 1);
    } else {
      couponTypeState.push(item.couponType);
    }
    changeSendSearchParams({
      couponType: couponTypeState.length > 0 ? couponTypeState.join(',') : '',
      searchValue: inputValue,
    });
    buriedPoint({
      ty: 'click',
      op: 'click_type',
      cp: 'coupon_center',
      pf: 'pc',
      data: {
        coupon_type: item.couponType,
      },
    });
  };

  useEffect(() => {
    if (userInfo?.customerLogin === 1 && !Utils.getLocal('lqShowGuide')) {
      // 只有当所有引导步骤的 ref 都存在时才显示引导
      if (catalogLiRef.current && brandLiRef.current && searchInputRef.current) {
        setShowGuide(true);
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
    }
  }, [userInfo?.customerLogin, catalogLiRef, brandLiRef, searchInputRef]);

  const changeSortColumn = (item) => {
    let params;
    if (item.name === '综合') {
      params = {
        sortType: '',
        sortColumn: '',
      };
    } else {
      params = {
        sortType: sendSearchParams.sortType === 'asc' && item.code === sendSearchParams.sortColumn ? 'desc' : 'asc',
        sortColumn: item.code,
      };
      const name = {
        金额: 'money',
        门槛: 'threshold',
      }[item.name];
      buriedPoint({
        ty: 'click',
        op: 'click_sort',
        cp: 'coupon_center',
        pf: 'pc',
        data: {
          sort_type: name,
        },
      });
    }
    changeSendSearchParams({
      ...params,
      searchValue: inputValue,
    });
  };

  const changeInputValue = (e) => {
    setInputValue(e.target.value);
    if (!e.target.value) {
      changeSendSearchParams({
        searchValue: '',
      });
    }
  };

  const changeSearchValue = () => {
    changeSendSearchParams({
      searchValue: inputValue,
    });
  };

  const checkSelectNum = (selectDataList, name) => {
    if (selectDataList.length > 10) {
      message.info(name + '项最多选择10个！');
      return false;
    }
    return true;
  };

  const changeSendParams = (selectDataList, name) => {
    if (name === '类目') {
      changeSendSearchParams({
        searchCatalogStr: selectDataList.map((item) => item.value).join('$'),
        catalogId: '',
        searchValue: inputValue,
      });
    } else if (name === '品牌') {
      changeSendSearchParams({
        searchBrandNameStr: selectDataList.map((item) => item.value).join('$'),
        searchValue: inputValue,
      });
    }
  };

  const onlyOpenOneShowMore = (name, value) => {
    const catalogFn = () => {
      if (value) {
        brandRef?.current?.resetInitStatus();
      }
    };
    const brandFn = () => {
      if (value) {
        catalogRef?.current?.resetInitStatus();
      }
    };

    const fnMap = {
      类目: catalogFn,
      品牌: brandFn,
    };
    typeof fnMap[name] === 'function' && fnMap[name]();
  };

  const changeSendCouponParams = (value) => {
    changeSendSearchParams({
      catalogId: value,
      searchCatalogStr: '',
      searchValue: inputValue,
    });
  };

  const CloseIcon = useMemo(() => {
    if (closeIconHoverStatus) {
      return 'lq-modal-close-active';
    }
    return 'lq-modal-close';
  }, [closeIconHoverStatus]);

  const changeStepNum = (value) => {
    if (value < 1 || value > 3) return;
    setStepNum(value);
  };

  const closeGuideMask = () => {
    setShowGuide(false);
    Utils.setLocal('lqShowGuide', true);
  };

  return (
    <>
      <div className={'flex flex-col'}>
        <ul>
          {catalogData.list.length > 0 && (
            <li className={'relative border-b border-solid border-[#EFEFEF] pt-[17px]'} ref={catalogLiRef}>
              <FilterConditionItem
                data={catalogData}
                ref={catalogRef}
                onlyOpenOneShowMore={onlyOpenOneShowMore}
                changeSendParams={changeSendParams}
                changeSendCouponParams={changeSendCouponParams}
                selectValue={sendSearchParams?.searchCatalogStr}
                couponValue={sendSearchParams?.catalogId}
                hasCheckAll={false}
                checkSelectNum={checkSelectNum}
                wrapCls={stepNum === StepEnums.one && showGuide ? 'relative z-[12]' : ''}
                disabled={showGuide}
              />
            </li>
          )}
          {brandData.list.length > 0 && (
            <li className={'relative pt-[10px] border-b border-solid border-[#EFEFEF]'} ref={brandLiRef}>
              <FilterConditionItem
                data={brandData}
                ref={brandRef}
                onlyOpenOneShowMore={onlyOpenOneShowMore}
                changeSendParams={changeSendParams}
                selectValue={sendSearchParams?.searchBrandNameStr}
                hasCheckAll={false}
                checkSelectNum={checkSelectNum}
                wrapCls={stepNum === StepEnums.second && showGuide ? 'relative z-[12]' : ''}
                disabled={showGuide}
              />
            </li>
          )}
        </ul>
        <div className={'flex  text-[14px] text-[#333333] pt-[10px]'}>
          <div className={'flex-1 flex items-center'}>
            <ul className={'flex'}>
              {SortListEnums.map((item) => {
                return (
                  <li
                    className={`flex items-center mr-[30px] cursor-pointer h-[32px] leading-[32px]`}
                    key={item.code}
                    onClick={() => changeSortColumn(item)}
                  >
                    <div
                      className={`hover:text-[#0093E6] ${sendSearchParams.sortColumn === item.code ? 'text-[#0093E6]' : ''}`}
                    >
                      {item.name}
                    </div>
                    {item.code !== '' && (
                      <StyledFilterCondition className={'flex-1 flex items-center flex-col ml-[3px]'}>
                        <div
                          className={`up-icon w-[6px] h-[5px] mb-[2px] ${sendSearchParams.sortColumn === item.code && sendSearchParams.sortType === 'asc' ? 'up-icon-a' : ''}`}
                        ></div>
                        <div
                          className={`down-icon w-[6px] h-[5px] ${sendSearchParams.sortColumn === item.code && sendSearchParams.sortType === 'desc' ? 'down-icon-a' : ''}`}
                        ></div>
                      </StyledFilterCondition>
                    )}
                  </li>
                );
              })}
            </ul>
            <ul className={'flex h-[32px] leading-[32px]'}>
              {couponTypeList.map((option) => (
                <li key={option.couponType} className={'mr-[30px]'}>
                  <CustomCheckbox
                    value={option.couponType}
                    checked={
                      sendSearchParams.couponType
                        ? sendSearchParams.couponType.split(',').includes(option.couponType)
                        : false
                    }
                    onChange={() => changeCouponType(option)}
                  >
                    <span className={'translate-y-[1px] inline-block'}>{option.couponTypeName}</span>
                  </CustomCheckbox>
                </li>
              ))}
            </ul>
          </div>
          <div className={'w-[218px] relative'} ref={searchInputRef}>
            <StyledSearchInput
              placeholder={'输入品牌/名称查询'}
              value={inputValue}
              maxLength={52}
              suffix={<span className={'search-icon size-[15px] cursor-pointer'} onClick={changeSearchValue}></span>}
              onChange={(e) => changeInputValue(e)}
              onPressEnter={changeSearchValue}
              className={`${stepNum === StepEnums.third && showGuide ? 'relative z-[12]' : ''}`}
              disabled={showGuide}
            />
          </div>
        </div>
        <GuideMask
          visible={showGuide}
          refValue={
            stepNum === StepEnums.one
              ? catalogLiRef?.current
              : stepNum === StepEnums.second
                ? brandLiRef?.current
                : searchInputRef?.current
          }
        >
          <Popover
            placement={stepNum === StepEnums.third ? 'bottomRight' : 'bottomLeft'}
            title={''}
            open={true}
            align={{
              offset: [0, -2],
            }}
            content={
              <>
                <div className={'flex justify-between mb-[8px] items-center'}>
                  <div className={'text-[14px] text-[#000000] font-semibold'}>
                    {stepNum === StepEnums.one && '可支持您按类目找券'}
                    {stepNum === StepEnums.second && '还可以按品牌找券'}
                    {stepNum === StepEnums.third && '还可以按关键词搜券'}
                  </div>
                  <div
                    onMouseEnter={() => setCloseIconHoverStatus(true)}
                    onMouseLeave={() => setCloseIconHoverStatus(false)}
                    onClick={closeGuideMask}
                  >
                    <SvgIcon app="index" iconName={CloseIcon} className={'cursor-pointer size-[10px]'} />
                  </div>
                </div>
                <div className={'w-[292px] text-[rgba(51,51,51,0.88)] text-[14px] mb-[16px]'}>
                  {stepNum === StepEnums.one && '精准找到您想要的品类全部优惠券'}
                  {stepNum === StepEnums.second && '快速找到您想要的品牌全部优惠券'}
                  {stepNum === StepEnums.third && '搜您想搜的所有优惠券'}
                </div>
                <div className={'flex justify-between items-center'}>
                  <div className={'flex'}>
                    {[StepEnums.one, StepEnums.second, StepEnums.third].map((item) => {
                      return (
                        <div
                          key={item}
                          className={`size-[6px] rounded-[6px] mr-[6px] ${stepNum === item ? 'bg-[#0093E6]' : 'bg-[#D9D9D9]'}`}
                        ></div>
                      );
                    })}
                  </div>
                  <div className={'text-center'}>
                    {stepNum === StepEnums.second && (
                      <button
                        className={
                          'w-[58px] text-[12px] h-[24px] text-center leading-[24px] mr-[10px] border border-solid border-[rgba(0,0,0,0.15)] rounded-[4px] hover:text-[#0093E6] hover:border-[#0093E6]'
                        }
                        onClick={() => {
                          changeStepNum(stepNum - 1);
                        }}
                      >
                        上一步
                      </button>
                    )}
                    {stepNum < StepEnums.third && (
                      <button
                        className={
                          'w-[58px] text-[12px] h-[24px] text-center leading-[24px]  bg-[#0093E6] text-white rounded-[4px] hover:opacity-80'
                        }
                        onClick={() => {
                          changeStepNum(stepNum + 1);
                        }}
                      >
                        下一步
                      </button>
                    )}
                    {stepNum === StepEnums.third && (
                      <button
                        className={
                          'w-[58px] text-[12px] h-[24px] text-center leading-[24px]  bg-[#0093E6] text-white rounded-[4px] hover:opacity-80'
                        }
                        onClick={closeGuideMask}
                      >
                        知道了
                      </button>
                    )}
                  </div>
                </div>
              </>
            }
          >
            {[StepEnums.one, StepEnums.second].includes(stepNum) ? (
              <div
                className={`w-[1200px] h-[49px] rounded-[10px] absolute ${stepNum === 1 ? 'top-[2px]' : ' top-[-4px]'} left-[-20px] z-[11] bg-white`}
              ></div>
            ) : (
              <div className={`w-[218px] h-[32px] rounded-[10px] absolute top-[0px] right-[0px] z-[11] bg-white`}></div>
            )}
          </Popover>
        </GuideMask>
      </div>
    </>
  );
};

export default memo(FilterCondition);
